import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { noop } from 'lodash';

import { IconButton } from 'components';
import { Col, Row } from 'components/layout';
import { CVUploadModalInstance } from 'components/candidate/CVUploadModal';
import Logo from 'components/Logo';
import Modal from 'components/Modal';
import { ButtonV3 } from 'components/ComponentV2';
import { MatchesFrequency } from 'components/candidate/MatchesFrequency/MatchesFrequency.component';
import { FLOW } from 'hooks/useApp';
import { useCandidate } from 'hooks/useCandidate';
import useScrollOffset from 'hooks/useScrollOffset';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import Matches from '../Matches';
import SidebarRight from '../SidebarRight';
import SidebarLeft from '../SidebarLeft';
import TodoSection from '../TodoSection';
import PastMatches from '../PastMatches';
import FeaturedOpportunitiesComponent from '../FeaturedOpportunities';

import EventReco from '../EventReco';
import OpportunityReco from '../OpportunityReco';
import DashboardBanner from '../DashboardBanner';
import AffiliatedSocieties from '../AffiliatedSocieties';

import styles from './Dashboard.module.scss';

export interface DashboardProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const Dashboard: React.FC<DashboardProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: DashboardProps) => {
    const contentClassNames = cn(
      {
        [styles.Dashboard]: true,
      },
      className
    );
    const router = useRouter();
    const { getScrollOffset } = useScrollOffset();

    const [isMatchingPreferences, setIsMatchingPreferences] = useState(false);
    const [showMPEmptyState, setMPEmptyState] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const PastMatchesRef = useRef<HTMLDivElement>(null);

    const viewportRef = useRef(document.documentElement);
    const headerRef = useRef<HTMLDivElement>(null);

    const scrollToPastMatches = () => {
      const topSpace = 20;
      const headerHeight = 62;
      const adjustment = topSpace + headerHeight;
      const offset = getScrollOffset(
        PastMatchesRef,
        viewportRef,
        headerRef,
        adjustment
      );
      window.scrollTo({ top: offset, behavior: 'smooth' });
    };
    const {
      updateCandidatesLastStep,
      refetchCandidateDashboard,
      candidateLastUpdatedStep,
    } = useCandidateDashboard();
    const { candidate } = useAuth();
    const { isNonOnboardedUser, isOldOnboardedUser } = useCandidate();
    const { matchesMeta, isMatchesRegenerating } = useCandidateMatches();
    const { total_count } = matchesMeta || {};

    const handleCVUpload = async () => {
      try {
        await CVUploadModalInstance();
        refetchCandidateDashboard();
      } catch (error) {
        console.error(error);
      }
    };

    const onOpenMatchingPreferencesModal = () => {
      setIsMatchingPreferences(true);
    };

    const onOpenOnboarding = () => {
      setIsMatchingPreferences(false);
    };

    useEffect(() => {
      if (isNonOnboardedUser && isOldOnboardedUser) {
        setIsMatchingPreferences(false);
      }
    }, [isNonOnboardedUser, isOldOnboardedUser]);

    // TODO: @mahak - check if this is the correct place to call refetchCandidateDashboard
    useEffect(() => {
      refetchCandidateDashboard();
    }, [isMatchingPreferences]);

    useEffect(() => {
      if (
        total_count !== undefined &&
        total_count === 0 &&
        candidateLastUpdatedStep === FLOW.MATCHING_PREFERENCES
      ) {
        setMPEmptyState(true);
      } else {
        setMPEmptyState(false);
      }
    }, [total_count, candidateLastUpdatedStep]);

    const newMatchesLoadingScreen = useMemo(() => {
      return (
        <Modal
          isOpen={isMatchesRegenerating}
          onRequestClose={noop}
          isFullScreen={true}
          className={styles.emptyStateMP}
          contentClassName={styles.emptyStateMPContainer}
        >
          <div className={styles.loadingpage}>
            <p className={styles.emoji}>🔎</p>
            <p className={styles.title}>Getting new Matches!</p>
            <p className={styles.desc1}>
              Hang tight, we’re crunching the numbers.
            </p>
            <div className={styles.separator}></div>
            <p className={styles.desc2}>
              Scanning 1000’s of positions to find the ones that fit your
              background and interests best.
            </p>
            <Image
              src="/images/loading.gif"
              alt="loader"
              width={120}
              height={120}
            />
            <p className={styles.desc3}>Searching the best companies...</p>
          </div>
        </Modal>
      );
    }, [isMatchesRegenerating]);

    const closeMPEmptyState = useCallback(async () => {
      try {
        setLoading(true);
        await updateCandidatesLastStep(FLOW.MP_EMPTY_MATCH);
        setLoading(false);
        setMPEmptyState(false);
      } catch (error) {
        console.error(error);
      }
    }, []);

    if (isMatchesRegenerating) return newMatchesLoadingScreen;

    return (
      <div className={contentClassNames} style={style} data-testid="Dashboard">
        <Row
          align="center"
          justify="space-between"
          className={cn(
            styles.headerRow,
            styles.hideTablet,
            styles.hideDesktop
          )}
        >
          <Logo name="huzzle-new" size="small" color="primary" />
          <IconButton
            iconName="icon_chat"
            size="large"
            className={styles.chatIcon}
            onClick={() => router.push(PAGE_ROUTES.CANDIDATE_MESSAGES)}
          />
        </Row>
        <DashboardBanner
          className={cn(styles.banner, styles.hideTablet, styles.hideDesktop)}
        />

        <Row
          justify="center"
          wrap={false}
          columnGap={20}
          className={styles.content}
        >
          <Col className={cn(styles.sidebar1, styles.hideTablet)}>
            <SidebarLeft
              onOpenMatchingPreferencesModal={onOpenMatchingPreferencesModal}
              cvUpload={handleCVUpload}
            />
          </Col>
          <Col className={styles.main}>
            <Matches
              onOpenMatchingPreferencesModal={onOpenMatchingPreferencesModal}
              onOpenOnboarding={onOpenOnboarding}
              className={cn(styles.matchesSection, styles.section)}
              onClickPastMatches={scrollToPastMatches}
            />
            <FeaturedOpportunitiesComponent />

            <AffiliatedSocieties />
            {candidate?.show_matches_frequency_banner && <MatchesFrequency />}
            <TodoSection />
            <div ref={PastMatchesRef}>
              <PastMatches />
            </div>
            <EventReco />
            <OpportunityReco />
          </Col>
          <Col className={cn(styles.hideMobile)}>
            <SidebarLeft
              onOpenMatchingPreferencesModal={onOpenMatchingPreferencesModal}
              cvUpload={handleCVUpload}
              className={cn(styles.hideMobile, styles.hideDesktop)}
            />
            <SidebarRight className={styles.sidebar2} />
          </Col>
        </Row>
        {showMPEmptyState && total_count === 0 ? (
          <Modal
            isOpen={showMPEmptyState}
            onRequestClose={() => setMPEmptyState(false)}
            isFullScreen={true}
            className={styles.emptyStateMP}
            contentClassName={styles.emptyStateMPContainer}
          >
            <div className={styles.contentWrapper}>
              <div className={styles.content}>
                <p className={styles.emoji}>😶‍🌫️</p>
                <p className={styles.title}>
                  There are no relevant opportunities right now
                </p>
                <p className={styles.desc}>
                  New opportunities are added every day, so hang tight and check
                  back soon. Change your preferences if you’d like different
                  matches now.
                </p>
              </div>
            </div>
            <div className={styles.buttons}>
              <ButtonV3
                startIcon="icon_arrow-left-2"
                startIconClassName={styles.startIcon}
                iconSize="mediumplus"
                size="large"
                borderRadius={12}
                isFullWidth={true}
                isLoading={isLoading}
                onClick={closeMPEmptyState}
              >
                Home
              </ButtonV3>
              <ButtonV3
                variant="text"
                className={styles.changePref}
                color="black"
                onClick={() => {
                  setMPEmptyState(false);
                  router.push(
                    `${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true`
                  );
                }}
              >
                Change Preferences
              </ButtonV3>
            </div>
          </Modal>
        ) : null}
      </div>
    );
  }
);

Dashboard.displayName = 'Dashboard';
